/**
 * Client-side image compression for prototype base64 uploads.
 * Downsizes long edge to 1400px and re-encodes as JPEG at ~0.78 quality.
 * Aims to keep the resulting file under ~500KB.
 */
export async function compressImage(file, { maxEdge = 1400, quality = 0.78 } = {}) {
  if (!file || !file.type?.startsWith("image/")) {
    throw new Error("Please pick an image file.");
  }
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = dataUrl;
  });

  let { width, height } = img;
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  width = Math.round(width * scale);
  height = Math.round(height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);

  let q = quality;
  let out = canvas.toDataURL("image/jpeg", q);
  // Aggressively shrink further if still large
  while (out.length > 550 * 1024 && q > 0.35) {
    q -= 0.1;
    out = canvas.toDataURL("image/jpeg", q);
  }

  const sizeBytes = Math.round((out.length * 3) / 4);
  return {
    fileName: (file.name || "photo") + ".jpg",
    mimeType: "image/jpeg",
    sizeBytes,
    data: out,
  };
}
